import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Users,
  MapPin,
  Star,
  Heart,
  ArrowRight
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const PalmBayPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Weekly, bi-weekly, and monthly cleaning for Palm Bay families. Same trusted team every visit.",
      link: "/services/house-cleaning"
    },
    {
      title: "Deep Cleaning",
      description: "Perfect for Florida's humidity and coastal conditions. Inside appliances, grout, baseboards, and more.",
      link: "/services/deep-cleaning"
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Helping Palm Bay renters and homeowners with inspection-ready cleaning during transitions.",
      link: "/services/move-in-move-out-cleaning"
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Fast turnovers for Palm Bay Airbnb and VRBO properties with guest-ready standards.",
      link: "/services/vacation-rental-cleaning"
    }
  ];

  const faqs = [
    {
      question: "Do you serve all of Palm Bay?",
      answer: "Yes. Palm Bay is our home base, and we serve all neighborhoods—from Bayside Lakes and Port Malabar to West Palm Bay, Malabar Road, and the communities near I-95. We know the area well because we live and work here."
    },
    {
      question: "What makes Pierce Premier different from other Palm Bay cleaning companies?",
      answer: "We're not a franchise or a large company with rotating crews. Bob and Heather personally oversee every job, and you'll see the same trusted cleaners every visit. We're your neighbors, and our reputation in this community matters to us."
    },
    {
      question: "Can you handle the humidity and Florida-specific cleaning challenges?",
      answer: "Absolutely. Heather's 15+ years of experience includes working in Florida homes. We understand mold prevention, dealing with sand and salt air for homes near the coast, and the constant battle with humidity. It's why so many Palm Bay families trust us with their homes."
    },
    {
      question: "Do you work with Palm Bay Realtors and property managers?",
      answer: "Yes. We work with local Realtors preparing listings, property managers handling turnovers, and landlords getting units ready for new tenants. If you need a reliable cleaning partner in Palm Bay, we're here."
    },
    {
      question: "Are you pet-friendly?",
      answer: "We love pets! Many of our Palm Bay clients have dogs, cats, and other animals. We'll work around feeding times, respect their space, and treat your furry family members with care."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Palm Bay, FL | Pierce Premier",
      description: "Trusted house cleaning in Palm Bay, FL. Bob & Heather are based right here in Palm Bay. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/palm-bay"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Palm Bay" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Palm Bay",
      description: "Professional house cleaning services in Palm Bay, FL including recurring cleaning, deep cleaning, move-in/move-out, and vacation rental turnover."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Palm Bay, FL | Pierce Premier"
        description="Trusted house cleaning in Palm Bay, FL. Bob & Heather are based in Palm Bay—same team every visit, 15+ years experience, fully insured, pet-friendly. Call 321-408-1347."
        canonical="/areas/palm-bay"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-cyan-600" />
                  <Badge className="bg-cyan-600 text-white">Our Hometown</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Palm Bay, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Palm Bay is our home. Bob and Heather live and work right here in the community, and we&apos;ve built Pierce Premier on trust, consistency, and personal service. When you hire us, you&apos;re not calling a national franchise or dealing with rotating crews—you&apos;re working with your neighbors.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  From Bayside Lakes to Port Malabar, from West Palm Bay to the neighborhoods near I-95, we know this city and the people who live here. That&apos;s why so many Palm Bay families trust Pierce Premier for their cleaning needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-cyan-600 text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional house cleaning services in Palm Bay, Florida"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-4 border-cyan-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <Heart className="w-8 h-8 text-cyan-600 fill-cyan-600" />
                    <div>
                      <div className="font-bold text-lg text-gray-900">Based in</div>
                      <div className="text-sm text-gray-600">Palm Bay</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Pierce Premier in Palm Bay */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Palm Bay Trusts Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                We&apos;re not just another cleaning company serving Palm Bay—this is where we live, raise our family, and invest in our community.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">This Is Our Home</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather aren&apos;t just based in Palm Bay—we live here. We shop at the same stores, know the same roads, and understand the challenges of maintaining a home in Central Florida&apos;s climate. Our reputation in this community is everything.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Trusted Team</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. No strangers in your home every week. When you hire Pierce Premier, you get the same cleaners every visit. They learn your home, your preferences, and become familiar faces you can trust.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Professional Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every Palm Bay home we serve. She knows what quality cleaning looks like and trains our team to the same high standards.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">We Care About Our Neighbors</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Many of our clients are our neighbors—literally. We see you at the grocery store, at community events, around town. That local accountability drives us to deliver quality work every single time.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Services for Palm Bay */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cleaning Services We Provide in Palm Bay
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                From recurring house cleaning to vacation rental turnovers, we handle it all.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-cyan-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                    <Link 
                      to={service.link}
                      className="inline-flex items-center text-cyan-600 font-semibold hover:text-cyan-700"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why We Love Serving Palm Bay */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Palm Bay
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Palm Bay isn&apos;t just where we work—it&apos;s where we live. Bob and Heather chose to build their business here because they believe in this community. We see our clients at Target on Malabar Road, grab coffee near Bayside Lakes, and take our own pets to the same local vet many of you do.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  When you hire Pierce Premier, you&apos;re not just getting a cleaning service—you&apos;re supporting a local family business that invests right back into Palm Bay. That local connection matters to us, and we hope it matters to you too.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Neighborhoods We Serve */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Palm Bay Neighborhoods We Serve
              </h2>
              <p className="text-gray-600 mb-8">
                We clean homes throughout Palm Bay, including:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Bayside Lakes",
                "Port Malabar",
                "West Palm Bay",
                "Malabar Road Area",
                "St. Johns Heritage",
                "Waterway Estates",
                "Palm Bay Estates",
                "Turkey Creek",
                "Minton Road Area",
                "I-95 Corridor"
              ].map((neighborhood, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{neighborhood}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32905, 32907, 32909, 32910
              </p>
              <p className="text-sm text-gray-500">
                Don&apos;t see your area? Call us—we likely serve your neighborhood!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Palm Bay Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Success Story */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-cyan-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Palm Bay Customer Success
                </h3>
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic text-center">
                  &quot;Pierce Premier did an amazing job with our move-out cleaning. The house looked brand new! Very professional and thorough.&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold text-center">Sarah Johnson</p>
                <p className="text-gray-500 text-sm text-center">Palm Bay, FL</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Experience the Pierce Premier Difference?"
          description="Let your Palm Bay neighbors Bob and Heather take care of your home. Same trusted team, quality work, and local accountability you can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default PalmBayPage;
